import React, { useState, useEffect } from 'react';
import { 
  Activity,
  LucidePieChart,
  BarChart3,
  RefreshCw,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PieChart as RechartsPieChart, Pie, Tooltip, Legend } from 'recharts';
import { API_URL } from '../../api/config';

const CHART_COLORS = {
  pie: ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#6366F1', '#EC4899', '#8B5CF6', '#14B8A6', '#F97316', '#A855F7'],
  bar: ['#4F46E5', '#7C3AED', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#EF4444', '#8B5CF6', '#14B8A6', '#F97316']
};

const SalesReport = () => {
  const { userToken } = useAuth();
  const navigate = useNavigate();

  const [salesData, setSalesData] = useState([]);
  const [itemStats, setItemStats] = useState([]);
  const [timeFilter, setTimeFilter] = useState('today');
  const [chartType, setChartType] = useState('pie');
  const [sortConfig, setSortConfig] = useState({ key: 'item', direction: 'asc' });

  // Subscription & loading states
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    checkSubscriptionStatus();
  }, [userToken]);

  useEffect(() => {
    if (subscriptionStatus === 'active') {
      fetchData();
    }
  }, [timeFilter, userToken, subscriptionStatus]);

  const checkSubscriptionStatus = async () => {
    try {
      setCheckingSubscription(true);
      const response = await fetch(`${API_URL}/subscription/status`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch subscription status');
      }
      const data = await response.json();
      setSubscriptionStatus(data.status);
      setSubscriptionDetails(data);
    } catch (error) {
      console.error('Error checking subscription status:', error);
    } finally {
      setCheckingSubscription(false);
    }
  };

  const handleSubscribe = () => {
    navigate('/subscription');
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const endDate = new Date();
      let startDate = new Date();
      switch (timeFilter) {
        case 'today':
          startDate.setHours(0, 0, 0, 0);
          break;
        case '7days':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case '1month':
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case '3months':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case '6months':
          startDate.setMonth(endDate.getMonth() - 6);
          break;
        case '1year':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        default:
          startDate.setHours(0, 0, 0, 0);
      }

      // Fetch sales data from the sales endpoint
      const response = await fetch(
        `${API_URL}/reports/item-sales?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`,
        {
          headers: { 'Authorization': `Bearer ${userToken}` }
        }
      );
      const salesResponse = await response.json();
      // Assuming the returned data is in salesResponse.data
      const sales = salesResponse.data || [];
      setSalesData(sales);

      // Process detailed item statistics based on sales data
      const stats = {};
      sales.forEach(record => {
        if (!stats[record.itemName]) {
          stats[record.itemName] = {
            item: record.itemName,
            quantity: record.totalQuantity,
            totalRevenue: record.totalRevenue,
            averagePrice: record.averagePrice
          };
        }
      });
      setItemStats(Object.values(stats));
    } catch (error) {
      console.error('Error fetching sales data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Aggregated stats for stat cards
  const totalSales = itemStats.reduce((sum, item) => sum + item.totalRevenue, 0);
  const totalItemsSold = itemStats.reduce((sum, item) => sum + item.quantity, 0);
  const averageSale = itemStats.length > 0 ? totalSales / itemStats.length : 0;

  const getSalesByItem = () => {
    return itemStats.map(item => ({
      name: item.item,
      value: item.totalRevenue || 0
    }));
  };

  const sortData = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedItems = () => {
    const sorted = [...itemStats];
    const { key, direction } = sortConfig;
    return sorted.sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getTimeFilterLabel = (filter) => {
    switch (filter) {
      case 'today': return 'Today';
      case '7days': return 'Last 7 Days';
      case '1month': return 'Last Month';
      case '3months': return 'Last 3 Months';
      case '6months': return 'Last 6 Months';
      case '1year': return 'Last Year';
      default: return filter;
    }
  };

  const PieChartComponent = () => {
    const data = getSalesByItem();
    const chartData = data.map((entry, index) => ({
      name: entry.name,
      value: entry.value,
      fill: CHART_COLORS.pie[index % CHART_COLORS.pie.length]
    }));

    return (
      <div className="flex flex-col items-center">
        <RechartsPieChart width={400} height={400}>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            outerRadius={160}
            paddingAngle={1}
            dataKey="value"
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            labelLine={false}
          />
          <Tooltip />
          <Legend />
        </RechartsPieChart>
        <div className="flex flex-wrap justify-center mt-4 gap-4">
          {chartData.map((entry, index) => (
            <div key={index} className="flex items-center">
              <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: entry.fill }}></div>
              <span className="text-sm text-gray-600">{entry.name}: ₹{entry.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const BarChartComponent = () => {
    const data = getSortedItems().slice(0, 10);
    const maxValue = Math.max(...data.map(item => item.totalRevenue));
    return (
      <div className="h-96 w-full">
        <svg viewBox="0 0 800 400" width="100%" height="100%">
          {/* Y-axis */}
          <line x1="60" y1="20" x2="60" y2="350" stroke="#e5e7eb" strokeWidth="1" />
          {/* X-axis */}
          <line x1="60" y1="350" x2="750" y2="350" stroke="#e5e7eb" strokeWidth="1" />
          {data.map((item, index) => {
            const barHeight = (item.totalRevenue / maxValue) * 300;
            const barWidth = 60;
            const x = 80 + index * (barWidth + 10);
            const y = 350 - barHeight;
            return (
              <g key={index}>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  fill={CHART_COLORS.bar[index % CHART_COLORS.bar.length]}
                  rx="4"
                />
                <text
                  x={x + barWidth / 2}
                  y={370}
                  textAnchor="middle"
                  fontSize="12"
                  fill="#4b5563"
                >
                  {item.item.length > 10 ? item.item.substring(0, 10) + '...' : item.item}
                </text>
                <text
                  x={x + barWidth / 2}
                  y={y - 10}
                  textAnchor="middle"
                  fontSize="12"
                  fill="#4b5563"
                >
                  ₹{Math.round(item.totalRevenue).toLocaleString()}
                </text>
              </g>
            );
          })}
          {[0, 0.25, 0.5, 0.75, 1].map((tick, index) => {
            const y = 350 - tick * 300;
            return (
              <g key={index}>
                <line x1="55" y1={y} x2="60" y2={y} stroke="#e5e7eb" strokeWidth="1" />
                <text x="45" y={y + 4} textAnchor="end" fontSize="10" fill="#6b7280">
                  ₹{Math.round(maxValue * tick).toLocaleString()}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    );
  };

  if (checkingSubscription) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-lg">Checking subscription status...</p>
        </div>
      </div>
    );
  }

  if (subscriptionStatus !== 'active') {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Premium Feature</h2>
          <div className="bg-indigo-50 p-4 rounded-lg mb-6">
            <span className="text-indigo-600 text-4xl">📊</span>
            <p className="text-gray-700 mt-3">
              The Sales Report is a premium feature that requires an active subscription.
            </p>
          </div>
          <button 
            onClick={handleSubscribe}
            className="w-full px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg shadow hover:bg-indigo-700 transition duration-200"
          >
            Subscribe Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white shadow-sm">
        <div className="flex justify-between items-center px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Activity className="mr-2" />
            Sales Report
          </h1>
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              className={`p-2 rounded-md ${chartType === 'pie' ? 'bg-indigo-600 text-white' : 'text-gray-600'}`}
              onClick={() => setChartType('pie')}
            >
              <LucidePieChart size={20} />
            </button>
            <button
              className={`p-2 rounded-md ${chartType === 'bar' ? 'bg-indigo-600 text-white' : 'text-gray-600'}`}
              onClick={() => setChartType('bar')}
            >
              <BarChart3 size={20} />
            </button>
          </div>
          <button onClick={fetchData} className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
            <RefreshCw size={16} />
            <span>Refresh</span>
          </button>
        </div>
      </header>
      <main className="px-6 py-6 flex-grow">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-medium text-gray-700">Time Period</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {['today', '7days', '1month', '3months', '6months', '1year'].map(filter => (
              <button
                key={filter}
                className={`px-4 py-2 rounded-md transition-colors ${
                  timeFilter === filter ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
                onClick={() => setTimeFilter(filter)}
              >
                {getTimeFilterLabel(filter)}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow border-l-4" style={{ borderLeftColor: '#4F46E5' }}>
                <h3 className="text-gray-500 text-sm font-medium">Total Sales</h3>
                <p className="text-2xl font-bold text-gray-800 mt-1">₹{totalSales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow border-l-4" style={{ borderLeftColor: '#10B981' }}>
                <h3 className="text-gray-500 text-sm font-medium">Total Items Sold</h3>
                <p className="text-2xl font-bold text-gray-800 mt-1">{totalItemsSold.toLocaleString('en-IN')}</p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow border-l-4" style={{ borderLeftColor: '#F59E0B' }}>
                <h3 className="text-gray-500 text-sm font-medium">Average Sale</h3>
                <p className="text-2xl font-bold text-gray-800 mt-1">₹{averageSale.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Sales Distribution</h2>
              {chartType === 'pie' ? <PieChartComponent /> : <BarChartComponent />}
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800">Item Sales Statistics</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        onClick={() => sortData('item')}
                        className="px-6 py-3 text-gray-500 font-medium text-sm uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center">
                          Item
                          {sortConfig.key === 'item' &&
                            (sortConfig.direction === 'asc' ? <ArrowUp size={14} className="ml-1" /> : <ArrowDown size={14} className="ml-1" />)}
                        </div>
                      </th>
                      <th
                        onClick={() => sortData('quantity')}
                        className="px-6 py-3 text-gray-500 font-medium text-sm uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center">
                          Quantity
                          {sortConfig.key === 'quantity' &&
                            (sortConfig.direction === 'asc' ? <ArrowUp size={14} className="ml-1" /> : <ArrowDown size={14} className="ml-1" />)}
                        </div>
                      </th>
                      <th
                        onClick={() => sortData('totalRevenue')}
                        className="px-6 py-3 text-gray-500 font-medium text-sm uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center">
                          Revenue
                          {sortConfig.key === 'totalRevenue' &&
                            (sortConfig.direction === 'asc' ? <ArrowUp size={14} className="ml-1" /> : <ArrowDown size={14} className="ml-1" />)}
                        </div>
                      </th>
                      <th
                        onClick={() => sortData('averagePrice')}
                        className="px-6 py-3 text-gray-500 font-medium text-sm uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center">
                          Avg Price
                          {sortConfig.key === 'averagePrice' &&
                            (sortConfig.direction === 'asc' ? <ArrowUp size={14} className="ml-1" /> : <ArrowDown size={14} className="ml-1" />)}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {getSortedItems().map((item, index) => (
                      <tr key={item.item} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800">{item.item}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.quantity.toLocaleString('en-IN')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          ₹{item.totalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          ₹{item.averagePrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {subscriptionDetails && (
              <div className="mt-6 bg-indigo-50 border border-indigo-100 rounded-lg p-4 flex justify-between items-center">
                <div className="text-indigo-800 font-medium">
                  {subscriptionDetails.plan === '1-month' && '1-Month Plan'}
                  {subscriptionDetails.plan === '6-months' && '6-Month Plan'}
                  {subscriptionDetails.plan === '1-year' && '1-Year Plan'}
                </div>
                <div className="text-indigo-600">
                  Valid until: {new Date(subscriptionDetails.endDate).toLocaleDateString()}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default SalesReport;
